import React, { useState, useEffect } from "react";
import { X, Calculator, ArrowRight } from "lucide-react";
import { useLoanModal } from "../../context/loan-modal-context";

export default function CalculatorModal({ isOpen, onClose, calculatorName = "EMI Calculator" }) {
  const { openLoanModal } = useLoanModal();

  // Calculator State Values (Defaults)
  const [amount, setAmount] = useState(500000); // Default ₹5 Lakhs
  const [interestRate, setInterestRate] = useState(10.5); // Default 10.5%
  const [tenureYears, setTenureYears] = useState(3); // Default 3 Years

  // Reset defaults or adjust when opened
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Standard EMI Formula: [P x R x (1+R)^N] / [(1+R)^N - 1]
  const principal = Number(amount) || 0;
  const monthlyRate = Number(interestRate) / 12 / 100;
  const totalMonths = (Number(tenureYears) || 1) * 12;

  let monthlyEmi = 0;
  let totalInterest = 0;
  let totalPayment = 0;

  if (principal > 0 && monthlyRate > 0 && totalMonths > 0) {
    const emiFactor = Math.pow(1 + monthlyRate, totalMonths);
    monthlyEmi = Math.round((principal * monthlyRate * emiFactor) / (emiFactor - 1));
    totalPayment = monthlyEmi * totalMonths;
    totalInterest = totalPayment - principal;
  }

  // Format numbers to Indian Currency String
  const formatINR = (val) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  // Map calculator names to official loan product names
  const mapCalculatorToProduct = (name = "") => {
    const lower = name.toLowerCase();
    if (lower.includes("business")) return "Business Loan";
    if (lower.includes("gold")) return "Gold Loan";
    if (lower.includes("ev") || lower.includes("vehicle") || lower.includes("car")) return "EV Loan";
    if (lower.includes("stamp") || lower.includes("property") || lower.includes("home") || lower.includes("lap")) {
      return "Loan Against Property";
    }
    return "Personal Loan";
  };

  // Handle Apply Now CTA Click
  const handleApplyClick = () => {
    onClose();
    if (openLoanModal) {
      const loanProduct = mapCalculatorToProduct(calculatorName);
      openLoanModal(loanProduct);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3.5 sm:p-5 overflow-y-auto">
      
      {/* 1. Backdrop Blur Overlay */}
      <div
        className="fixed inset-0 bg-[#07130e]/70 backdrop-blur-md transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* 2. Modal Dialog Container */}
      <div className="relative w-full max-w-[720px] rounded-[24px] sm:rounded-[28px] border border-[#e2eae5] bg-white shadow-[0_25px_60px_rgba(0,0,0,0.25)] overflow-hidden z-10 my-auto animate-in fade-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-[#eef2ef] bg-[#f7faf8] px-5 py-4 sm:px-7 sm:py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 aspect-square items-center justify-center rounded-xl bg-[var(--hero-green-light)] text-[var(--hero-green)] border border-[var(--hero-green-border)]">
              <Calculator size={20} />
            </div>
            <div>
              <span className="font-jakarta text-[11px] font-bold text-[var(--hero-green)] tracking-wider uppercase">
                CALCULATOR
              </span>
              <h3 className="font-bricolage text-[18px] sm:text-[22px] font-bold text-[#111827] leading-tight">
                {calculatorName}
              </h3>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#e2eae5] bg-white text-[#64748b] hover:bg-gray-100 hover:text-black transition-colors cursor-pointer"
            aria-label="Close Calculator"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 sm:p-7 space-y-6">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-7 items-start">
            
            {/* Left Column: Sliders & Controls (7 Cols) */}
            <div className="lg:col-span-7 space-y-5">
              
              {/* 1. Loan Amount Slider */}
              <div className="rounded-2xl border border-[#e5ebe7] bg-[#fbfdfc] p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="font-jakarta text-[12.5px] font-semibold text-[#475569]">
                    Loan Amount
                  </label>
                  <span className="font-bricolage text-[16px] sm:text-[17px] font-bold text-[var(--hero-green)]">
                    {formatINR(amount)}
                  </span>
                </div>
                <input
                  type="range"
                  min="50000"
                  max="5000000"
                  step="10000"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-full h-2 bg-[#d7e5dc] rounded-lg appearance-none cursor-pointer accent-[var(--hero-green)]"
                />
                <div className="flex justify-between text-[11px] font-jakarta text-[#94a3b8] mt-1.5 font-medium">
                  <span>₹50K</span>
                  <span>₹25 Lakh</span>
                  <span>₹50 Lakh</span>
                </div>
              </div>

              {/* 2. Interest Rate Slider */}
              <div className="rounded-2xl border border-[#e5ebe7] bg-[#fbfdfc] p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="font-jakarta text-[12.5px] font-semibold text-[#475569]">
                    Interest Rate (% p.a.)
                  </label>
                  <span className="font-bricolage text-[16px] sm:text-[17px] font-bold text-[var(--hero-green)]">
                    {interestRate}%
                  </span>
                </div>
                <input
                  type="range"
                  min="7.5"
                  max="24"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  className="w-full h-2 bg-[#d7e5dc] rounded-lg appearance-none cursor-pointer accent-[var(--hero-green)]"
                />
                <div className="flex justify-between text-[11px] font-jakarta text-[#94a3b8] mt-1.5 font-medium">
                  <span>7.5%</span>
                  <span>15%</span>
                  <span>24%</span>
                </div>
              </div>

              {/* 3. Tenure Slider */}
              <div className="rounded-2xl border border-[#e5ebe7] bg-[#fbfdfc] p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="font-jakarta text-[12.5px] font-semibold text-[#475569]">
                    Loan Tenure (Years)
                  </label>
                  <span className="font-bricolage text-[16px] sm:text-[17px] font-bold text-[var(--hero-green)]">
                    {tenureYears} {tenureYears === 1 ? "Year" : "Years"} ({tenureYears * 12} Mons)
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="7"
                  step="1"
                  value={tenureYears}
                  onChange={(e) => setTenureYears(Number(e.target.value))}
                  className="w-full h-2 bg-[#d7e5dc] rounded-lg appearance-none cursor-pointer accent-[var(--hero-green)]"
                />
                <div className="flex justify-between text-[11px] font-jakarta text-[#94a3b8] mt-1.5 font-medium">
                  <span>1 Year</span>
                  <span>4 Years</span>
                  <span>7 Years</span>
                </div>
              </div>

            </div>

            {/* Right Column: Calculated Results Summary (5 Cols) */}
            <div className="lg:col-span-5 flex flex-col justify-between h-full rounded-2xl border border-[#d6e8dd] bg-[#f3f9f6] p-5 sm:p-5.5">
              
              <div>
                {/* Result Title */}
                <div className="text-center pb-4 border-b border-[#d8e6de]">
                  <p className="font-jakarta text-[11.5px] font-semibold uppercase tracking-wider text-[#64748b]">
                    Monthly EMI
                  </p>
                  <p className="mt-1 font-bricolage text-[28px] sm:text-[32px] font-bold text-[#111827] leading-none">
                    {formatINR(monthlyEmi)}
                  </p>
                </div>

                {/* Breakdown Details */}
                <div className="mt-4 space-y-3 font-jakarta text-[13px]">
                  <div className="flex justify-between items-center text-[#475569]">
                    <span>Principal Amount</span>
                    <span className="font-bold text-[#1e293b]">{formatINR(principal)}</span>
                  </div>

                  <div className="flex justify-between items-center text-[#475569]">
                    <span>Total Interest</span>
                    <span className="font-bold text-[#1e293b]">{formatINR(totalInterest)}</span>
                  </div>

                  <div className="pt-2 border-t border-[#d8e6de] flex justify-between items-center">
                    <span className="font-semibold text-[#1e293b]">Total Payable</span>
                    <span className="font-bold text-[14.5px] text-[var(--hero-green)]">
                      {formatINR(totalPayment)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Button: Apply for this Loan */}
              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleApplyClick}
                  className="w-full flex items-center justify-center gap-2 rounded-full bg-[var(--hero-green)] py-3 px-5 font-jakarta text-[14px] font-semibold text-white shadow-md transition-all hover:bg-[var(--hero-green-hover)] hover:shadow-lg cursor-pointer"
                >
                  <span>Apply For This Loan</span>
                  <ArrowRight size={15} />
                </button>
              </div>

            </div>

          </div>

        </div>

      </div>
    </div>
  );
}
